import React, { useMemo } from 'react';
import _ from 'lodash';
import { formatNumber, Loading } from '@tradingblock/components';
import { toShort } from '@tradingblock/api';
import {
  CashieringView,
  Transfer,
  AchRelationship,
  TransferInstruction,
  CashieringDetailTransfer,
  TaxWithholdingType,
  TransferData,
} from '@tradingblock/types';
import {
  formatDateOfDeath,
  getDeliveryMethodLabel,
  getDistributionDescription,
  getFormattedAchRelationship,
  getFormattedTransferIRADistributionReason,
  getFormattedTransferInstruction,
  isNotYetCreatedOrPendingStatus,
} from '../../utilities/cashiering';
import { TradingBlockLink } from '../basic/TradingBlockLink';
import { CashieringEntityOwnerNames } from './CashieringEntityOwnerNames';
import { useStateSelector } from '../../data/global/dataSelectors';
import { CashieringEntityDetails } from './CashieringEntityDetails';
import { cleanDateModel } from '../../utilities/date';
import styled from 'styled-components';

// Styled components for grid layout
const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--gap);
  margin-bottom: var(--gap);

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }

  /* Ensure sections align properly */
  > * {
    align-self: start;
  }
`;

const GridSection = styled.div`
  background: var(--blue-dark);
  border-radius: var(--radius);
  border: 1px solid var(--lighten);
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease-in-out;
  height: fit-content;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }
`;

const GridSectionHeader = styled.div`
  background: var(--blue-dark);
  padding: var(--gap);
  border-bottom: 1px solid var(--lighten);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  h3 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 300;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 0.5em;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }
`;

const GridSectionContent = styled.div`
  background: var(--blue-dark);
  padding: var(--gap);
  color: #fff;

  /* Consistent text styling */
  label {
    font-weight: 600;
    color: #fff;
    margin-right: 0.5rem;
    font-size: 0.98rem;
  }

  .mute {
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.98rem;
    font-weight: 400;
  }

  .warn {
    background: rgba(255, 193, 7, 0.1);
    border: 1px solid rgba(255, 193, 7, 0.3);
    border-radius: var(--radius);
    padding: calc(var(--gap) / 3);
    margin: calc(var(--gap) / 3) 0;
    color: var(--orange);
    font-size: 0.9rem;
  }

  .btn-link {
    background: none;
    border: none;
    color: var(--blue);
    text-decoration: underline;
    cursor: pointer;
    padding: 0;
    margin: 0;
    font-size: inherit;

    &:hover {
      color: var(--teal);
    }
  }

  /* Consistent spacing */
  > div {
    margin-bottom: 0;
  }

  > div:last-child {
    margin-bottom: 0;
  }

  /* Better spacing for nested elements */
  .mb-3 {
    margin-bottom: calc(var(--gap) / 2);
  }

  .mt-3 {
    margin-top: calc(var(--gap) / 2);
  }

  /* Ensure consistent value styling */
  span:not(.mute):not([style*='color: var(--teal)']):not([style*='color: var(--orange)']):not([style*='color: var(--green')]) {
    color: rgba(255, 255, 255, 0.85);
    font-weight: 400;
  }

  /* Fix descriptive text styling */
  .txt-sm {
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.9rem;
    font-weight: 400;
  }

  /* Consistent fields-title styling */
  .fields-title {
    color: #fff;
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  /* Override prop-title and prop-value styling for cashiering */
  .prop-title {
    font-size: 0.98rem;
    font-weight: 600;
    color: #fff;
    margin-right: 0.5rem;
  }

  .prop-value {
    font-size: 0.98rem;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.4;
  }

  /* Ensure prop-stacked layout works correctly */
  .prop-stacked {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
`;

// Legacy section styling for backward compatibility
const LegacySection = styled.div`
  margin-bottom: var(--gap);
`;

interface AccountWithdrawalDisplayProps {
  title?: string;
  nickname: string;
  availableForWithdrawal: number;
  availableForTrading: number;
  cashBalance?: number;
  marginBalance?: number;
  positions?: number;
  transfer?: TransferData;
  transferDetails?: CashieringDetailTransfer;
  achRelationship: AchRelationship | undefined;
  transferInstructions: TransferInstruction | undefined;
  adminReview?: boolean;
  isPendingFullWithdrawal?: boolean;
  gotoNextView?: (view: string) => void;
  useGridLayout?: boolean; // New prop for grid layout
}

export const AccountWithdrawalDisplay: React.FC<AccountWithdrawalDisplayProps> = ({
  title,
  nickname,
  availableForWithdrawal,
  isPendingFullWithdrawal,
  availableForTrading,
  positions,
  transfer,
  transferDetails,
  achRelationship,
  transferInstructions,
  adminReview,
  gotoNextView,
  children,
  cashBalance,
  marginBalance,
  useGridLayout = false,
}) => {
  const amount = useMemo(() => transfer && (transfer.amount || transfer.requestedAmount), [transfer]);
  const fieldTitle = useMemo(() => (title ? title : 'How much to withdraw?'), []);
  const subAccounts = useStateSelector(s => s.account.subaccounts.subaccounts);
  const subAccountTransferValues = transfer && transfer.subAccountsValues;
  const subAccountIds: string[] = Object.keys(subAccountTransferValues || {});
  const origin = useStateSelector(s => s.auth.origin);
  const isSaving = useStateSelector(s => s.cashiering.forms.transfer.isSaving);
  const status = useStateSelector(s => s.cashiering.forms.transfer.status);
  const isSuccessful = status === 'success';

  const subAccountDataAndValues = useMemo(() => {
    const subAccountValues = subAccountIds.map(id => {
      const subAccount = _.find(subAccounts, s => s.Id === parseInt(id));
      const transferAmount = subAccountTransferValues && subAccountTransferValues[id];
      return {
        subAccount,
        transferAmount,
      };
    });
    return subAccountValues;
  }, [subAccountIds, subAccounts, subAccountTransferValues]);

  const transferAmount = useMemo(() => {
    if (transfer && transfer.disbursementType === 'FullBalance') {
      return availableForWithdrawal;
    }
    // always return as a number

    if (typeof amount === 'string') {
      let amountValue = amount;
      // remove possible preceding '$'
      if (amount[0] === '$') {
        amountValue = amount.slice(1);
      }
      return parseFloat(amountValue);
    }

    return amount;
  }, [transfer, amount, availableForWithdrawal]);

  const renderTaxWithholding = (
    withholding: { value?: number | undefined; valueType?: TaxWithholdingType | undefined } | undefined,
    type: string
  ) => {
    // Check if withholding is undefined or if the value is not provided
    if (!withholding || withholding.value === undefined) {
      return <span>None</span>;
    }
    const formattedValue =
      withholding.valueType === TaxWithholdingType.Percent
        ? `${!adminReview ? withholding.value : withholding.value * 100}%`
        : formatNumber(withholding.value, { currency: true });

    if (withholding.valueType === TaxWithholdingType.Percent && transferAmount) {
      const calculatedValue = formatNumber(
        (!adminReview ? withholding.value / 100 : withholding.value) * (transferAmount || 0),
        { currency: true }
      );
      return `${formattedValue} (${calculatedValue})`;
    }

    return formattedValue;
  };

  // Render withdrawal amount section
  const renderWithdrawalAmount = () => (
    <div>
      <div>
        <label>Withdrawing from:</label> <span className="mute">{nickname}</span>
      </div>
      <div>
        <label>Available for withdrawal:</label>{' '}
        <span style={{ fontWeight: 600, fontSize: '1.15em', color: 'var(--teal)' }}>
          {formatNumber(availableForWithdrawal, { currency: true })}
        </span>
      </div>
      {adminReview && isPendingFullWithdrawal && (
        <div className="warn">
          <strong>Note:</strong> This request is for a full balance withdrawal.{' '}
          <span style={{ fontWeight: 600, fontSize: '1.15em', color: 'var(--teal)' }}>
            {formatNumber(availableForWithdrawal, { currency: true })}
          </span>{' '}
          will be withdrawn from this account.
        </div>
      )}
      <div>
        <label>Available for trading:</label>{' '}
        <span style={{ fontWeight: 600, fontSize: '1.05em', color: 'var(--lighten3x)' }}>
          {formatNumber(availableForTrading, { currency: true })}
        </span>
      </div>
      {!_.isUndefined(positions) && positions !== 0 && (
        <div>
          <label>Positions:</label>{' '}
          <span style={{ fontWeight: 600, fontSize: '1.05em', color: 'var(--teal)' }}>
            {formatNumber(positions, { currency: true })}
          </span>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <label style={{ margin: 0 }}>Amount:</label>
        {transfer && transfer.disbursementType === 'FullBalance' && (
          <>
            <span style={{ fontWeight: 700, fontSize: '1.2em', color: 'var(--teal)' }}>Full Cash Balance</span>
            <span className="mute">
              (
              <span style={{ fontWeight: 600, color: 'var(--teal)' }}>
                {formatNumber(availableForWithdrawal, { currency: true })}
              </span>
              )
            </span>
          </>
        )}
        {transfer && transfer.disbursementType !== 'FullBalance' && (
          <>
            {amount && (!subAccountDataAndValues || _.isEmpty(subAccountDataAndValues)) && (
              <>
                <span style={{ fontWeight: 700, fontSize: '1.2em', color: 'var(--teal)' }}>
                  {formatNumber(amount, { currency: true })}
                </span>
                {adminReview && (
                  <>
                    &ndash; <span style={{ fontWeight: 600, color: 'var(--lighten3x)' }}>partial withdrawal</span>
                  </>
                )}
              </>
            )}
            {amount && !_.isEmpty(subAccountDataAndValues) && (
              <>
                {subAccountDataAndValues.map(({ subAccount, transferAmount }) => (
                  <span key={`${subAccount && subAccount.Id}`}>
                    {subAccount && transferAmount !== 0 && (
                      <>
                        <label className="mute" style={{ margin: 0 }}>
                          {subAccount.Nickname}:
                        </label>
                        <span style={{ fontWeight: 600, color: 'var(--teal)' }}>
                          {transferAmount && formatNumber(transferAmount, { currency: true })}
                        </span>
                      </>
                    )}
                  </span>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <label className="mute" style={{ margin: 0 }}>
                    Total:
                  </label>
                  <span style={{ fontWeight: 700, color: 'var(--teal)' }}>
                    {amount && formatNumber(amount, { currency: true })}
                  </span>
                </div>
              </>
            )}
          </>
        )}
      </div>
      {gotoNextView && (
        <div>
          <button type="button" className="btn-link" onClick={() => gotoNextView(CashieringView.Type)}>
            <i className="fal fa-pencil" /> Change
          </button>
        </div>
      )}
    </div>
  );

  // Render fund reception section
  const renderFundReception = () => (
    <div>
      {transfer && transfer.mechanism === 'Ach' && (
        <div>
          <div>
            <label>Linked bank account:</label>
            {achRelationship && getFormattedAchRelationship(achRelationship)}
          </div>
          {adminReview && transfer.id && <CashieringEntityOwnerNames id={transfer.id} type="transfers" />}
          {adminReview && achRelationship && isNotYetCreatedOrPendingStatus(achRelationship.state) && (
            <div className="warn">This relationship is pending.</div>
          )}
        </div>
      )}

      {transfer && transfer.mechanism === 'Wire' && transferInstructions && (
        <>
          <div>
            <label className="mb-3">
              {transferInstructions.recipientBank.identifierType === 'BIC' ? 'International' : 'Domestic'} Wire Transfer
            </label>
            {transferInstructions && getFormattedTransferInstruction(transferInstructions)}
          </div>
          {adminReview && transfer.id && <CashieringEntityOwnerNames id={transfer.id} type="transfers" />}
          {adminReview && transfer.id && <CashieringEntityDetails id={transferInstructions.id} />}
          {adminReview && (
            <div>
              <span className="mute">This Wire Account was added on</span> {toShort(transferInstructions.createDate)}
            </div>
          )}
        </>
      )}

      {transfer && transfer.mechanism === 'Check' && (
        <>
          <div>
            <label>Paper check:</label> <span className="mute">Deliver to account name and address</span>
          </div>
          {adminReview && transfer.id && <CashieringEntityOwnerNames id={transfer.id} type="transfers" />}
          <div>
            <label>Delivery method:</label>{' '}
            <span className="mute">{getDeliveryMethodLabel(transfer.deliveryMethod)}</span>
            {!adminReview && transfer.deliveryMethod === 'Overnight' && (
              <>
                <div className="mute mt-3">
                  Overnight express mail will be delivered to the physical street address on file for your account (not
                  a P.O. Box).{' '}
                  <TradingBlockLink to={origin === 'mb' ? 'MBFeesUrl' : 'TBFeesUrl'}>
                    Delivery fees may apply
                  </TradingBlockLink>
                  . Requests made after 3 PM Central (US) will be processed the next business day.
                </div>
              </>
            )}
          </div>
          <div>
            <label>Memo to print on the check:</label>

            {_.some(transfer.memos, m => !!m) ? (
              _.map(_.filter(transfer.memos, m => !!m), (m, i) => (
                <div key={`memo${i}`} className="mb-3 mute">
                  {m}
                </div>
              ))
            ) : (
              <div className="mute">--None--</div>
            )}
          </div>
        </>
      )}

      {gotoNextView && (
        <div>
          <button type="button" className="btn-link" onClick={() => gotoNextView(CashieringView.Type)}>
            <i className="fal fa-pencil" /> Change
          </button>
        </div>
      )}
    </div>
  );

  // Render IRA distribution section
  const renderIRADistribution = () => (
    <div>
      <div className="mb-3">
        <label>Reason:</label>{' '}
        <span className="mute">
          {getFormattedTransferIRADistributionReason(
            transfer && transfer.iraDistribution && transfer.iraDistribution.reason
          )}
        </span>
        <div className="txt-sm mute">
          {getDistributionDescription(transfer && transfer.iraDistribution && transfer.iraDistribution.reason)}
        </div>
        {transfer && transfer.iraDistribution && transfer.iraDistribution.dateOfDeath && (
          <div className="mt-2">
            <label>Date of Death:</label>{' '}
            <span className="mute">{formatDateOfDeath(transfer.iraDistribution.dateOfDeath as any)}</span>
          </div>
        )}
      </div>

      <div>
        <label>Federal Tax Withholding:</label>{' '}
        {!isSaving && !isSuccessful ? (
          <span className="mute">
            {renderTaxWithholding(
              transfer && transfer.iraDistribution && transfer.iraDistribution.federalTaxWithholding,
              'federal'
            )}
          </span>
        ) : (
          <Loading size="small" />
        )}
      </div>

      <div className="mb-3">
        <label>State Tax Withholding:</label>{' '}
        {!isSaving && !isSuccessful ? (
          <span className="mute">
            {renderTaxWithholding(
              transfer && transfer.iraDistribution && transfer.iraDistribution.stateTaxWithholding,
              'state'
            )}
          </span>
        ) : (
          <Loading size="small" />
        )}
      </div>
      {gotoNextView && (
        <div>
          <button type="button" className="btn-link" onClick={() => gotoNextView(CashieringView.Type)}>
            <i className="fal fa-pencil" /> Change
          </button>
        </div>
      )}
    </div>
  );

  if (useGridLayout) {
    return (
      <>
        <GridContainer>
          <GridSection>
            <GridSectionHeader>
              <h3>
                <i className="fal fa-money-bill-wave" style={{ marginRight: '8px' }}></i>
                {fieldTitle}
              </h3>
            </GridSectionHeader>
            <GridSectionContent>{renderWithdrawalAmount()}</GridSectionContent>
          </GridSection>

          <GridSection>
            <GridSectionHeader>
              <h3>
                <i className="fal fa-arrow-down" style={{ marginRight: '8px' }}></i>
                How to Receive Funds
              </h3>
            </GridSectionHeader>
            <GridSectionContent>{renderFundReception()}</GridSectionContent>
          </GridSection>

          {transfer && transfer.iraDistribution && (
            <GridSection>
              <GridSectionHeader>
                <h3>
                  <i className="fal fa-file-invoice-dollar" style={{ marginRight: '8px' }}></i>
                  IRA Distribution Details
                </h3>
              </GridSectionHeader>
              <GridSectionContent>{renderIRADistribution()}</GridSectionContent>
            </GridSection>
          )}
        </GridContainer>
        {children}
      </>
    );
  }

  // Legacy vertical layout
  return (
    <>
      <LegacySection className="screen-body-section">
        <div>{fieldTitle}</div>
        {renderWithdrawalAmount()}
      </LegacySection>

      <LegacySection className="screen-body-section">
        <h2>How {adminReview ? '' : 'would you like'} to receive the funds?</h2>
        {renderFundReception()}
      </LegacySection>

      {transfer && transfer.iraDistribution && (
        <LegacySection className="screen-body-section">
          <h2>IRA Distribution Details</h2>
          {renderIRADistribution()}
        </LegacySection>
      )}

      {children}
    </>
  );
};
